import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import PreviewContent from './PreviewContent';

export type PreviewType = 'image' | 'pdf' | 'text' | 'video' | 'audio';

interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  viewUrl: string;
  previewType: PreviewType;
}

const IMAGE_EXTENSIONS = /\.(gif|jpe?g|png|tiff?)$/i;
const DOCX_EXTENSIONS = /\.(docx?|rtf|odt)$/i;
const PDF_EXTENSIONS = /\.pdf$/i;
const TEXT_EXTENSIONS = /\.(txt|csv)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|webm|mov)$/i;
const AUDIO_EXTENSIONS = /\.(mp3|wav|m4a|wma)$/i;

export const isImageFile = (fileName: string) =>
  IMAGE_EXTENSIONS.test(fileName);

export const isDocxFile = (fileName: string) => DOCX_EXTENSIONS.test(fileName);

export const isPdfFile = (fileName: string) => PDF_EXTENSIONS.test(fileName);

export const isTextFile = (fileName: string) => TEXT_EXTENSIONS.test(fileName);

export const isVideoFile = (fileName: string) =>
  VIDEO_EXTENSIONS.test(fileName);

export const isAudioFile = (fileName: string) =>
  AUDIO_EXTENSIONS.test(fileName);

interface OpenDocumentOptions {
  displayId?: string;
}

export const openDocument = (
  fileName: string,
  viewUrl: string,
  options?: OpenDocumentOptions
): PreviewType | false => {
  if (isDocxFile(fileName)) {
    const params = new URLSearchParams({ url: viewUrl, filename: fileName });
    if (options?.displayId) params.set('displayId', options.displayId);
    window.open(`/docx-viewer?${params}`, '_blank', 'noopener,noreferrer');
    return false;
  }
  if (isImageFile(fileName)) return 'image';
  if (isPdfFile(fileName)) return 'pdf';
  if (isTextFile(fileName)) return 'text';
  if (isVideoFile(fileName)) return 'video';
  if (isAudioFile(fileName)) return 'audio';

  window.open(viewUrl, '_blank', 'noopener,noreferrer');
  return false;
};

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  onClose,
  fileName,
  viewUrl,
  previewType,
}) => (
  <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
    <DialogContent className="max-w-4xl w-[90vw] flex flex-col gap-0 p-0 overflow-hidden">
      <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-gray-200">
        <DialogTitle className="truncate pr-8">{fileName}</DialogTitle>
        <DialogDescription className="sr-only">
          Preview of {fileName}
        </DialogDescription>
      </DialogHeader>
      <div className="flex items-center justify-center p-6 max-h-[80vh] overflow-auto">
        <PreviewContent
          previewType={previewType}
          viewUrl={viewUrl}
          fileName={fileName}
          open={open}
        />
      </div>
    </DialogContent>
  </Dialog>
);

export default DocumentPreviewModal;
